/**
 * Build the best-available link for a supplier row.
 *
 * Priority:
 *   1. Use the stored `supplier_url` when set — that's the canonical
 *      product page (DigiKey/Mouser include marketing slugs we can't
 *      reconstruct, and LCSC writes a deterministic URL on lookup).
 *   2. Fall back to a per-distributor search URL synthesized from the
 *      distributor PN. Most spreadsheet-imported parts arrive without
 *      `supplier_url`; without this fallback, clicking the PN took the
 *      user to the distributor's home page (bug #80).
 *   3. Last resort: the distributor's home page. Same as the original
 *      behavior — kept for distributors we don't have a PN-based URL
 *      pattern for.
 *
 * Returns `null` when none of the above can produce a URL.
 */

const SEARCH_URL_BUILDERS: Record<string, (pn: string) => string> = {
  // LCSC's product-page URL is fully deterministic from the PN, so
  // the fallback lands directly on the product page (the search
  // result IS the product page for an exact-PN match).
  lcsc: (pn) => `https://www.lcsc.com/product-detail/${encodeURIComponent(pn)}.html`,
  // DigiKey's product URLs include a marketing slug we can't
  // synthesize from a PN alone, so the fallback uses their
  // PN-aware result endpoint, which redirects to the product page
  // when there's a single exact match.
  digikey: (pn) => `https://www.digikey.com/en/products/result?s=${encodeURIComponent(pn)}`,
  // Mouser's catalog search likewise resolves single-PN matches
  // directly to the product page.
  mouser: (pn) => `https://www.mouser.com/c/?q=${encodeURIComponent(pn)}`,
  // Arrow's product URL pattern is /(en/)?products/<mpn-slug>/<mfr-slug>
  // — we don't know the mfr slug from the PN alone, so fall through
  // to their catalog search endpoint, which redirects to the product
  // page on a single match. Same shape as DigiKey/Mouser.
  arrow: (pn) => `https://www.arrow.com/en/products/search?q=${encodeURIComponent(pn)}`,
};

export interface SupplierLinkInput {
  supplier_url?: string | null;
  distributor_slug?: string | null;
  distributor_pn?: string | null;
  distributor_website?: string | null;
}

export function supplierLink(s: SupplierLinkInput): string | null {
  if (s.supplier_url && s.supplier_url.trim()) {
    return s.supplier_url;
  }
  const slug = s.distributor_slug ?? '';
  const pn = (s.distributor_pn ?? '').trim();
  const builder = SEARCH_URL_BUILDERS[slug];
  if (builder && pn) {
    return builder(pn);
  }
  return s.distributor_website ?? null;
}
